import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'

// ---- tipi sincronizzati con MappingProposal (backend) ----

export interface MappingProposal {
  source_columns: string[]
  proposed_mapping: Record<string, string>   // {source_col: canonical_field}
  unmapped_columns: string[]
  confidence_scores: Record<string, number>  // {source_col: 0.0 – 1.0}
  warnings: string[]
  /** US-1.3: mapping confermato dall'upload precedente per questa fonte dati. Null se primo upload. */
  saved_mapping: Record<string, string> | null
}

export interface ConfirmMappingPayload {
  mapping: Record<string, string>
}

// ---- hooks ----

/**
 * Recupera la proposta di mapping per un job in stato "mapping".
 * Endpoint: GET /api/v1/jobs/{jobId}/mapping
 */
export function usePreviewMapping(jobId: string | null) {
  return useQuery<MappingProposal>({
    queryKey: ['mapping-proposal', jobId],
    queryFn: () => api.get<MappingProposal>(`/api/v1/jobs/${jobId}/mapping`),
    enabled: !!jobId,
    staleTime: Infinity, // la proposta non cambia finché il job è in attesa
  })
}

/**
 * Recupera il mapping salvato in memoria per una fonte dati.
 * Endpoint: GET /api/v1/mappings/{dataSourceId}
 * Usato dal MappingEditor per mostrare il badge "Mapping salvato".
 */
export interface SavedMappingResponse {
  data_source_id: string
  mapping: Record<string, string>
  saved_at: string
  columns_count: number
}

export function useSavedMapping(dataSourceId: string | null | undefined) {
  return useQuery<SavedMappingResponse>({
    queryKey: ['saved-mapping', dataSourceId],
    queryFn: () => api.get<SavedMappingResponse>(`/api/v1/mappings/${dataSourceId}`),
    enabled: !!dataSourceId,
    staleTime: 1000 * 60 * 5, // 5 minuti — il mapping salvato cambia raramente
    retry: false, // 404 = no mapping salvato, non è un errore da riprare
  })
}

/**
 * Conferma il mapping utente e riprende il job di ingestion.
 * Endpoint: POST /api/v1/jobs/{jobId}/confirm-mapping
 */
export function useConfirmMapping(jobId: string) {
  return useMutation<void, Error, ConfirmMappingPayload>({
    mutationFn: (payload) =>
      api.post<void>(`/api/v1/jobs/${jobId}/confirm-mapping`, payload),
  })
}

// ---- Named mappings ----

export interface NamedMappingItem {
  id: string
  name: string
  columns_count: number
  updated_at: string | null
}

export interface NamedMappingDetail extends NamedMappingItem {
  mapping: Record<string, string>
  created_at: string
}

export interface CreateNamedMappingPayload {
  name: string
  mapping: Record<string, string>
}

/**
 * Lista i template mapping nominati del tenant.
 * Endpoint: GET /api/v1/named-mappings
 */
export function useNamedMappings() {
  return useQuery<NamedMappingItem[]>({
    queryKey: ['named-mappings'],
    queryFn: () => api.get<NamedMappingItem[]>('/api/v1/named-mappings'),
    staleTime: 1000 * 60 * 2,
  })
}

/**
 * Crea un template mapping nominato.
 * Endpoint: POST /api/v1/named-mappings
 */
export function useCreateNamedMapping() {
  const queryClient = useQueryClient()
  return useMutation<NamedMappingDetail, Error, CreateNamedMappingPayload>({
    mutationFn: (payload) =>
      api.post<NamedMappingDetail>('/api/v1/named-mappings', payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['named-mappings'] })
    },
  })
}

/**
 * Applica un template nominato a un job in stato 'mapping'.
 * Endpoint: POST /api/v1/named-mappings/{templateId}/apply?job_id={jobId}
 */
export function useApplyNamedMapping() {
  return useMutation<void, Error, { templateId: string; jobId: string }>({
    mutationFn: ({ templateId, jobId }) =>
      api.post<void>(`/api/v1/named-mappings/${templateId}/apply?job_id=${jobId}`, {}),
  })
}

/**
 * Elimina un template nominato.
 * Endpoint: DELETE /api/v1/named-mappings/{templateId}
 */
export function useDeleteNamedMapping() {
  const queryClient = useQueryClient()
  return useMutation<void, Error, string>({
    mutationFn: (templateId) =>
      api.delete<void>(`/api/v1/named-mappings/${templateId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['named-mappings'] })
    },
  })
}

// ---- Template certificati Koremargin (US-1.4) ----

export interface PlatformMappingTemplate {
  id: string
  name: string
  description: string | null
  template_source: string | null
  mapping: Record<string, string>
  columns_count: number
  updated_at: string | null
}

/**
 * Lista i template di mapping certificati da Koremargin.
 * Visibili a tutti i tenant, senza distinzione di piano.
 * Endpoint: GET /api/v1/platform-mapping-templates
 */
export function usePlatformMappingTemplates() {
  return useQuery<PlatformMappingTemplate[]>({
    queryKey: ['platform-mapping-templates'],
    queryFn: () => api.get<PlatformMappingTemplate[]>('/api/v1/platform-mapping-templates'),
    staleTime: 1000 * 60 * 15, // 15 minuti — i template certificati cambiano raramente
  })
}

/**
 * Applica un template certificato a un job in stato 'mapping'.
 * Endpoint: POST /api/v1/platform-mapping-templates/{templateId}/apply?job_id={jobId}
 */
export function useApplyPlatformTemplate() {
  return useMutation<void, Error, { templateId: string; jobId: string }>({
    mutationFn: ({ templateId, jobId }) =>
      api.post<void>(`/api/v1/platform-mapping-templates/${templateId}/apply?job_id=${jobId}`, {}),
  })
}
